import React from 'react';
import { motion } from 'framer-motion';
import { Handshake, Shield, Lightbulb, TrendingUp } from 'lucide-react';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import CountUp from 'react-countup';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
  const navigate = useNavigate();
  const features = [
    {
      image: '/assest/aboutus/Client.jfif',
      title: "Client-First Philosophy",
      desc: "We don't just deliver software; we deliver success. Our 'Partners, Not Vendors' approach means we're invested in your growth."
    },
    {
      image: '/assest/aboutus/Security.jfif',
      title: "Security & Reliability",
      desc: "Your data is protected with enterprise-grade security. We prioritize system reliability and performance."
    },
    {
      image: '/assest/aboutus/Innovation.jfif',
      title: "Innovation Meets Stability",
      desc: "We leverage cutting-edge technologies while maintaining rock-solid stability. Future-ready yet dependable."
    },
    {
      image: '/assest/aboutus/Scalable.jfif',
      title: "Scalable Growth",
      desc: "From startup to enterprise, our solutions grow with you. Flexible architectures that adapt to your needs."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer { animation: shimmer 5s infinite; }
      `}</style>
      <Navbar />

      {/* 2. HERO SECTION */}
      <section className="relative min-h-[100vh] flex items-center pt-32 md:pt-40">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: "url('/assest/logo.jfif')" }}
        >
          <div className="absolute inset-0 bg-[#0a1628]/75"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-[#EFCD86] uppercase tracking-wide font-semibold mb-4 text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
                About Us
              </h3>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                Pioneering Digital Excellence Since 1996
              </h1>
              <p className="text-2xl text-[#EFCD86] font-light">
                Transforming Businesses Through Innovative Software Solutions
              </p>
            </motion.div>

            {/* Right Side */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6 text-gray-300 text-lg"
            >
              <div style={{ lineHeight: 1.2 }}>
                <h1 className="text-3xl md:text-4xl font-bold text-[#EFCD86] mb-0 leading-tight">
                  INTRODUCTION
                </h1>
                <p >
                  For over 28 years, MakSoft has been at the forefront of digital innovation, crafting bespoke software solutions that drive real business growth.
                </p>
              </div>
              <div style={{ lineHeight: 1.2 }}>
                <h1 className="text-3xl md:text-4xl font-bold text-[#EFCD86] mb-0 leading-tight">
                  OUR STORY
                </h1>
                <p>
                  Born from a vision to bridge the gap between complex technology and business simplicity, we've evolved from a budding startup in 1996 to a trusted technology partner for businesses across industries.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. FOUR FEATURE CARDS SECTION */}
      <section className="py-24 relative overflow-hidden">
        {/* Subtle Glow Behind Cards */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-96 bg-[#EFCD86]/10 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="group bg-[#0d1f33]/80 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-[#EFCD86]/50 hover:shadow-2xl hover:shadow-[#EFCD86]/10 transition-all duration-500 hover:-translate-y-2 relative"
              >
                <div className="h-48 overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f33]/80 to-transparent z-10 opacity-60 pointer-events-none transition-opacity duration-500 group-hover:opacity-40"></div>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 relative z-0"
                  />
                </div>
                <div className="p-6 relative z-20">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#EFCD86] transition-colors">{feature.title}</h3>
                  <div className="w-12 h-0.5 bg-[#EFCD86] mb-3 transition-all duration-500 group-hover:w-20"></div>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. GOLD STATS BAR */}
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#EFCD86] via-[#d4b876] to-[#EFCD86] py-16 shadow-[0_0_40px_rgba(239,205,134,0.15)] mx-4 lg:mx-auto max-w-7xl my-24">
        {/* Shimmer overlay */}
        <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-slate-800/20 md:divide-transparent">
            {[
              { number: 28, suffix: '+', label: 'Years Experience' },
              { number: 500, suffix: '+', label: 'Businesses Served' },
              { number: 100, suffix: '%', label: 'Commitment to Quality' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
                className="group cursor-default py-4 md:py-0 relative"
              >
                {/* Vertical Divider Animation for md+ screens */}
                {index !== 0 && (
                  <motion.div
                    initial={{ height: 0 }}
                    whileInView={{ height: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="hidden md:block absolute left-0 top-0 w-px bg-gradient-to-b from-transparent via-slate-800/20 to-transparent"
                  />
                )}

                <div className="text-6xl font-black text-slate-900 group-hover:text-black transition-colors">
                  <CountUp end={stat.number} duration={2.5} suffix={stat.suffix} enableScrollSpy scrollSpyOnce />
                </div>
                <div className="mt-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-800 group-hover:text-slate-900">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. IMAGE/TEAM SECTION */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video md:aspect-[21/9] bg-slate-800 border border-white/5"
          >
            {/* Office Image */}
            <img
              src="/assest/aboutus/office.jfif"
              alt="MakSoft Office"
              className="w-full h-full object-cover"
            />

            {/* Subtle gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/50 to-transparent"></div>
          </motion.div>
        </div>
      </section>

      {/* 6. PRODUCTS ROW */}
      {/* <section className="py-24 border-y border-white/5 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.h3
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gray-100 font-semibold tracking-widest uppercase mb-12 text-sm md:text-base"
          >
            Products
          </motion.h3>

         <div className="space-y-5 text-left">
            {[
              [
                'Data Rescue & Recovery',
                'Hosting & Email',
                'Association Mgmt',
                'Audit Firm',
                'Backup Services',
              ],
              [
                'Chemical Mfg',
                'Chemical/Dyes',
                'Cost Accounting',
                'Cotton Solution',
                'Blinds/Windows Mfg',
              ],
              [
                'Distribution Network',
                'Dyeing Solution',
                'Electronics Solution',
                'Mobile Phone',
                'Medicine Wholesale',
              ],
              [
                'Medicine Retail',
                'Pharmacy Solution',
                'Oil Brokerage',
                'Online Store',
                'Payroll (Bio Metric)',
              ],
              [
                'Pharma Manufacturing',
                'Point Of Sale',
                'Sales Control Online',
                'Security Management',
                'Spinning Solution',
              ],
              [
                'Store Management',
                'Weaving Solution',
                'SMS Services',
                'Web Design',
              ],
            ].map((group, groupIndex) => (
              <div key={groupIndex}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {group.map((product, index) => {
                    const globalIndex = groupIndex * 5 + index;
                    return (
                      <motion.div
                        key={product}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        whileHover={{ scale: 1.03, y: -2 }}
                        transition={{ delay: globalIndex * 0.04, duration: 0.3, type: "spring", stiffness: 300 }}
                        viewport={{ once: true }}
                        className="relative rounded-lg border border-[#FFFFFF]/20 bg-[#091527]/80 px-3 py-2 shadow-md shadow-[#000000]/30 hover:border-[#FFFFFF] hover:bg-[#0d1f33] hover:shadow-[#6DBF57]/20 hover:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden"
                      >
                        
                        
                        
                        <h4 className="text-xs font-medium text-[#EFCD86] leading-snug group-hover:text-[#FFFFFF] transition-colors duration-300">{product}</h4>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* 7. CTA SECTION */}
      <section className="relative py-32 flex items-center justify-center text-center">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: "url('/assest/logo.jfif')" }}
        >
          <div className="absolute inset-0 bg-[#0a1628]/90 backdrop-blur-sm"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Ready to Transform Your Business?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="text-xl text-gray-400 mb-10"
          >
            Let's build something extraordinary together.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <button 
              onClick={() => navigate('/contact')}
              className="px-8 py-4 rounded-full border-2 border-[#EFCD86] text-[#EFCD86] bg-transparent hover:bg-[#EFCD86]/10 transition-all duration-300 font-medium w-full sm:w-auto"
            >
              Contact Us
            </button>
            <button 
              onClick={() => navigate('/services')}
              className="px-8 py-4 rounded-full bg-gradient-to-r from-[#046A8C] to-[#67B253] text-white font-semibold hover:shadow-lg hover:shadow-[#046A8C]/30 transition-all duration-300 w-full sm:w-auto"
            >
              View Our Services
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
